import {
  EnvToLocaleToConfig,
  Feature,
  FeatureToggleConfig,
} from '@shopee-user/feature-toggle';
import { Locale } from '@shopee-user/global';
import { isVersionReleased } from './utils/version';
import {
  VERSION_RELEASE_LOGIN_WITH_3P_ANDROID,
  VERSION_RELEASE_LOGIN_WITH_3P_IOS,
  VERSION_RELEASE_LOGIN_WITH_SHOPEE_ANDROID,
  VERSION_RELEASE_LOGIN_WITH_SHOPEE_IOS,
} from './constants/versionRelease';
import { getPlatformType, PlatformType } from '@shopee/web-bridge-sdk';

// getPlatformType() function call should be safe to be called here
// it doesn't rely on any bridge function. Just check the user-agent
const isIOS = getPlatformType() === PlatformType.iOS;

import {
  GENERAL_BASE_CONFIG,
  LOGIN_BY_PASSWORD_BASE_CONFIG,
  LOGIN_BY_OTP_BASE_CONFIG,
  LOGIN_BY_BIOMETRIC_BASE_CONFIG,
  SIGNUP_BY_OTP_BASE_CONFIG,
  RESET_PASSWORD_BASE_CONFIG,
  SHOPEE_PAY_MIGRATION_BASE_CONFIG,
  SHOPEE_PARTNER_SPECIFIC_BASE_CONFIG,
  WEBVIEW_BASE_CONFIG,
  USER_ACCOUNT_BASE_CONFIG,
  USER_PHONE_BASE_CONFIG,
  USER_EMAIL_BASE_CONFIG,
  USER_PASSWORD_BASE_CONFIG,
  OTP_SHARED_SERVICE_BASE_CONFIG,
  ONE_CREDENTIAL_BASE_CONFIG,
  ONE_CREDENTIAL_MIGRATION_BASE_CONFIG,
  ONE_CREDENTIAL_SWITCH_TOC_BASE_CONFIG,
  TOB_BRANDING_CONFIG,
  TEMPORARY_BASE_CONFIG,
  IVS_BASE_CONFIG,
  THIRDPARTY_INTEGRATION_BASE_CONFIG,
  ONE_CREDENTIAL_DELINK_BASE_CONFIG,
  NO_COST_LOGIN_METHOD_BASE_CONFIG,
} from '@tob/shared/feature-toggle-base-config';

const OneCredentialFeatureConfig: EnvToLocaleToConfig = {
  default: {
    default: false,
    [Locale.ID]: true,
    [Locale.SG]: true,
    [Locale.VN]: true,
  },
};

const IvsFeatureToggleConfig = true;

const AuthenticationByPasswordFeatureConfig = true;

export const FEATURE_TOGGLE_CONFIG: FeatureToggleConfig = {
  ...GENERAL_BASE_CONFIG,
  ...LOGIN_BY_PASSWORD_BASE_CONFIG,
  ...LOGIN_BY_OTP_BASE_CONFIG,
  ...LOGIN_BY_BIOMETRIC_BASE_CONFIG,
  ...SIGNUP_BY_OTP_BASE_CONFIG,
  ...RESET_PASSWORD_BASE_CONFIG,
  ...SHOPEE_PAY_MIGRATION_BASE_CONFIG,
  ...SHOPEE_PARTNER_SPECIFIC_BASE_CONFIG,
  ...WEBVIEW_BASE_CONFIG,
  ...USER_ACCOUNT_BASE_CONFIG,
  ...USER_PHONE_BASE_CONFIG,
  ...USER_EMAIL_BASE_CONFIG,
  ...USER_PASSWORD_BASE_CONFIG,
  ...OTP_SHARED_SERVICE_BASE_CONFIG,
  ...ONE_CREDENTIAL_BASE_CONFIG,
  ...ONE_CREDENTIAL_MIGRATION_BASE_CONFIG,
  ...ONE_CREDENTIAL_SWITCH_TOC_BASE_CONFIG,
  ...ONE_CREDENTIAL_DELINK_BASE_CONFIG,
  ...TOB_BRANDING_CONFIG,
  ...TEMPORARY_BASE_CONFIG,
  ...THIRDPARTY_INTEGRATION_BASE_CONFIG,
  ...IVS_BASE_CONFIG,
  ...NO_COST_LOGIN_METHOD_BASE_CONFIG,

  // General
  [Feature.WebTracking]: {
    default: {
      default: false,
      [Locale.ID]: true,
      [Locale.SG]: true,
      [Locale.VN]: true,
      [Locale.MY]: true,
    },
  },
  [Feature.AppTracking]: true,
  [Feature.LoginTermsOfService]: {
    default: {
      default: false,
      [Locale.SG]: true,
      [Locale.VN]: true,
      [Locale.TH]: true,
      [Locale.MY]: true,
      [Locale.PH]: true,
      [Locale.ID]: true,
    },
  },

  // Login by password
  [Feature.LoginByPasswordEntrance]: AuthenticationByPasswordFeatureConfig,
  [Feature.LoginByPasswordAllowEmail]: true,
  [Feature.LoginByPasswordAllowPhone]: true,
  [Feature.LoginByPasswordRestrictPasswordLength]: true,
  [Feature.LoginByPasswordShowLoginTip]: {
    default: {
      default: false,
      [Locale.ID]: false,
      [Locale.SG]: true,
    },
  },
  [Feature.LoginByPasswordLayoutRefactor]: true,
  [Feature.LoginByPasswordOneCredentialLayoutRefactor]: true,
  // Login by OTP
  [Feature.LoginByOtpEntrance]: {
    default: {
      default: false, // note: currently only VN and ID have OTP Login
      [Locale.VN]: true,
      [Locale.ID]: true,
    },
  },
  [Feature.LoginByOtpAllowPhone]: true,
  // Signup by OTP
  [Feature.SignupByOtpEntrance]: {
    default: {
      [Locale.TH]: true,
      [Locale.VN]: true,
      [Locale.BR]: true,
      [Locale.ID]: true,
      [Locale.PH]: true,
      [Locale.MY]: true,
      default: false,
    },
  },
  [Feature.SignupByOtpEnableOneCredential]: {
    default: {
      default: true,
      [Locale.TH]: false, // note: TH is not integrated with one credentials yet
      [Locale.BR]: false, // note: BR is not integrated with one credentials yet
      [Locale.PH]: false, // note: PH is not integrated with one credentials yet
      [Locale.MY]: false, // note: MY is not integrated with one credentials yet
    },
  },
  [Feature.SignupByOtpAllowEmail]: {
    default: {
      default: false, // We have not enabled one credentials signup with email yet
      [Locale.TH]: false, // For TH, sign up allows Phone only
      [Locale.BR]: true, // BR is not integrated with one credentials yet, so we allow login by email for BR.
      [Locale.MY]: true,
    },
  },
  [Feature.SignupByOtpHideSignupTip]: {
    default: {
      default: false,
      [Locale.TH]: true,
      [Locale.BR]: true,
    },
  },
  [Feature.SignupByOtpAllowPhone]: true,
  [Feature.SignupByOtpRequireAcceptTermsAndService]: true,
  [Feature.SignupByOtpRequirePassword]: AuthenticationByPasswordFeatureConfig,
  [Feature.SignupByOtpCustomiseResendOtpMessage]: true,
  [Feature.SignupByOtpLayoutRefactor]: true,
  [Feature.SignupByOtpLoginUponAlreadyRegisteredWithPhone]: true,

  // Reset password
  [Feature.ResetPasswordEntrance]: AuthenticationByPasswordFeatureConfig,
  [Feature.ResetPasswordByPhone]: true,
  [Feature.ResetPasswordByEmail]: true,
  [Feature.ResetPasswordCallResetPasswordInit]: true,

  // User phone
  [Feature.UserPhoneEntrance]: true,
  [Feature.UserAddPhone]: true,

  // User email
  [Feature.UserEmailEntrance]: true,
  [Feature.UserAddEmail]: true,

  // User password
  [Feature.UserPasswordEntrance]: AuthenticationByPasswordFeatureConfig,

  // One credential
  [Feature.OneCredential]: OneCredentialFeatureConfig,
  [Feature.OneCredentialOptional]: OneCredentialFeatureConfig,
  [Feature.OneCredentialAllowPhone]: OneCredentialFeatureConfig,
  // One credential migration
  [Feature.OneCredentialMigrationEntrance]: OneCredentialFeatureConfig,
  [Feature.OneCredentialMigrationAllowStaffToChangePhoneAndEmail]: true,
  // One credential delink
  [Feature.OneCredentialDelinkEntrance]: OneCredentialFeatureConfig,
  [Feature.OneCredentialDelinkAllowPhone]: true,
  [Feature.OneCredentialDelinkSetPasswordEnabled]: AuthenticationByPasswordFeatureConfig,

  [Feature.SPXOrangeBackgroundUI]: {
    default: {
      default: false,
      [Locale.VN]: true,
      [Locale.TH]: true,
      [Locale.PH]: true,
      [Locale.SG]: true,
      [Locale.MY]: true,
      [Locale.BR]: true,
    },
  },

  // Webview
  [Feature.NotifyRefreshToken]: true,
  [Feature.WebViewNotifySignupSuccess]: true,

  // Thirdparty Integration
  // CCMS toggle `EnableThirdpartyIntegrationSupportShopeeInApp` will be checked as well
  [Feature.ThirdpartyIntegrationSupportShopeeInApp]: isIOS
    ? isVersionReleased(VERSION_RELEASE_LOGIN_WITH_SHOPEE_IOS)
    : isVersionReleased(VERSION_RELEASE_LOGIN_WITH_SHOPEE_ANDROID),
  // CCMS toggle `EnableThirdpartyIntegrationSupport<ThirdPartyAuthenticationType>InWeb` will be checked as well
  [Feature.ThirdpartyIntegrationSupportShopeeInWeb]: true,
  [Feature.ThirdpartyIntegrationSupportGoogleInWeb]: true,
  [Feature.ThirdpartyIntegrationSupportFacebookInWeb]: true,
  [Feature.ThirdpartyIntegrationSupportAppleInWeb]: true,
  [Feature.ThirdpartyIntegrationSupportGoogleInApp]: isIOS
    ? isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_IOS)
    : isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_ANDROID),
  [Feature.ThirdpartyIntegrationSupportFacebookInApp]: isIOS
    ? isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_IOS)
    : isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_ANDROID),
  [Feature.ThirdpartyIntegrationSupportAppleInApp]: isIOS
    ? isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_IOS)
    : isVersionReleased(VERSION_RELEASE_LOGIN_WITH_3P_ANDROID),

  // User linkage
  [Feature.UserAccountLinkage]: true, // linkage feature also relies on Thirdparty integration toggles

  // IVS
  [Feature.IvsIntegration]: IvsFeatureToggleConfig,
  [Feature.VerifyOtpSharedService]: IvsFeatureToggleConfig,
  [Feature.VerifyPasswordSharedService]: IvsFeatureToggleConfig,
  [Feature.IvsHideShopeeBranding]: true,

  // Third-party bind shared service
  [Feature.ThirdPartyBindingSharedServiceEntrance]: true,
};
